import { useMutation, useQuery, useQueryClient } from "react-query";
import { t } from "@/i18n-js/instance";
import {
  reactQueryDelete,
  reactQueryGet,
  reactQueryPost,
  reactQueryPut,
} from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { useToast } from "@circle-react-shared/uikit/ToastV2";

const INDEX_QUERY_KEY = ["internal_api", "community_bot", "snippets"];

interface BulkDeleteSnippetRequest {
  ids: number[];
}

interface UseModalProps {
  onSuccess: () => void;
}

interface EditSnippetRequest {
  id: number;
  title: string;
}

interface ApiEditSnippetRequest {
  title: string;
}

interface ApiEditSnippetResponse {
  id: number;
  title: string;
}

interface Snippet {
  title: string;
  body: string;
}

interface SnippetItem {
  id: number;
  title: string;
  body: string;
}

export interface CommunityBotProfilePreview {
  id: number;
  name: string;
  avatar_url: string;
}

export interface CommunityMemberPreview {
  id: number;
  name: string;
  avatar_url?: string;
}

export interface SnippetPreview {
  id: number;
  title: string;
  body: object;
  created_at: string;
  updated_at: string;
  type: string;
  file_url: string;
  file_size: string;
  truncated: boolean;
  used_in_replies: number;
  community_bot_profiles: CommunityBotProfilePreview[];
  creator?: CommunityMemberPreview;
  updater?: CommunityMemberPreview;
}

interface SnippetProp {
  id: number;
  name: string;
  body: object;
}

interface SnippetResponse {
  records: Array<SnippetProp>;
  count: number;
}

export const useIndexSnippets = ({
  currentPage,
  title,
  type,
}: {
  currentPage: number;
  title?: string;
  type?: string;
}) =>
  useQuery<SnippetResponse>({
    queryKey: INDEX_QUERY_KEY,
    queryFn: () =>
      reactQueryGet(
        internalApi.communityBot.snippets.index({
          params: {
            page: currentPage,
            per_page: 30,
            ...(title ? { title } : {}),
            ...(type ? { type } : {}),
          },
        }),
      ),
  });

export const useShowSnippet = ({ id }: { id: number }) =>
  useQuery<SnippetItem, Error>(["snippets", id], () =>
    reactQueryGet(internalApi.communityBot.snippets.show({ id })),
  );

export const useShowPreviewSnippet = ({ id }: { id: number }) =>
  useQuery<SnippetPreview, Error>(["snippets", id, "preview"], () =>
    reactQueryGet(internalApi.communityBot.snippets.showPreview({ id })),
  );

export const useBulkDeleteSnippets = ({ onSuccess }: UseModalProps) => {
  const queryClient = useQueryClient();
  const { success, error } = useToast();

  return useMutation<void, Error, BulkDeleteSnippetRequest>(
    (data: BulkDeleteSnippetRequest) =>
      reactQueryDelete(internalApi.communityBot.snippets.bulkDestroy(), {
        ids: data.ids,
      }),
    {
      onSuccess: (_, variables) => {
        void queryClient.invalidateQueries(INDEX_QUERY_KEY);
        variables.ids?.forEach(id => {
          void queryClient.invalidateQueries(["snippet", id]);
        });
        onSuccess();
        success(
          t(
            "settings.community_bot.knowledge.custom.resources_deleted_success",
            {
              count: variables.ids?.length,
            },
          ),
        );
      },
      onError: () => {
        error(t("request_failure_message"));
      },
    },
  );
};

export const useEditSnippet = ({ onSuccess }: UseModalProps) => {
  const queryClient = useQueryClient();
  const { success, error } = useToast();

  return useMutation<ApiEditSnippetResponse, Error, EditSnippetRequest>(
    (data: EditSnippetRequest) => {
      const { id, ...rest } = data;
      const apiData: ApiEditSnippetRequest = {
        ...rest,
      };
      return reactQueryPut(
        internalApi.communityBot.snippets.update({ id }),
        apiData,
      );
    },
    {
      onSuccess: data => {
        void queryClient.invalidateQueries(INDEX_QUERY_KEY);
        void queryClient.invalidateQueries(["snippets", data.id]);
        onSuccess();
        success(t("changes_saved"));
      },
      onError: () => {
        error(t("request_failure_message"));
      },
    },
  );
};

export const useCreateSnippet = ({ onSuccess }: { onSuccess: () => void }) => {
  const { success, error } = useToast();
  const queryClient = useQueryClient();

  return useMutation(
    (formData: Snippet) =>
      reactQueryPost(internalApi.communityBot.snippets.create(), {
        snippet: formData,
      }),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries(INDEX_QUERY_KEY);
        success(
          t("settings.community_bot.knowledge.custom.resource_created_success"),
        );
        onSuccess();
      },
      onError: () => error(t("quick_post.toasts.failed_to_create")),
    },
  );
};

export const useCreateFromFilesSnippet = ({
  onSuccess,
}: {
  onSuccess: () => void;
}) => {
  const { success, error } = useToast();
  const queryClient = useQueryClient();

  return useMutation(
    (files: string[]) =>
      reactQueryPost(internalApi.communityBot.snippets.createFromFiles(), {
        files,
      }),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries(INDEX_QUERY_KEY);
        success(t("settings.community_bot.knowledge.custom.files_uploaded"));
        onSuccess();
      },
      onError: () => error(t("quick_post.toasts.failed_to_create")),
    },
  );
};
